import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { Toaster } from '@/components/ui/sonner';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import LandingPage from '@/pages/LandingPage';
import DashboardPage from '@/pages/DashboardPage';
import CitizensPage from '@/pages/CitizensPage';
import FinancePage from '@/pages/FinancePage';
import UsersPage from '@/pages/UsersPage';
import SettingsPage from '@/pages/SettingsPage';
import MeetingsPage from '@/pages/MeetingsPage';
import GuestReportPage from '@/pages/GuestReportPage';
import ComplaintPage from '@/pages/ComplaintPage';
import KelilingPage from '@/pages/KelilingPage';
import PublicDashboardPage from '@/pages/PublicDashboardPage';
import AnnouncementsPage from '@/pages/AnnouncementsPage';
import CitizenPortalPage from '@/pages/CitizenPortalPage';
import ActivityLogsPage from '@/pages/ActivityLogsPage';
import '@/App.css';

const LoadingScreen = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Memuat sistem...</p>
        </div>
    </div>
);

// For staff portal (non-warga roles)
const StaffRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === 'warga') return <Navigate to="/warga" replace />;
    return children;
};

// For citizen portal (warga role)
const WargaRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    return children;
};

const AppRoutes = () => {
    const { user } = useAuth();
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={
                user ? (user.role === 'warga' ? <Navigate to="/warga" replace /> : <Navigate to="/dashboard" replace />) : <LoginPage />
            } />
            <Route path="/public" element={<PublicDashboardPage />} />
            <Route path="/guest-report" element={<GuestReportPage />} />

            {/* Citizen portal */}
            <Route path="/warga" element={<WargaRoute><CitizenPortalPage /></WargaRoute>} />

            {/* Staff portal — uses Layout */}
            <Route element={<StaffRoute><Layout /></StaffRoute>}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/citizens" element={<CitizensPage />} />
                <Route path="/finance" element={<FinancePage />} />
                <Route path="/keliling" element={<KelilingPage />} />
                <Route path="/meetings" element={<MeetingsPage />} />
                <Route path="/complaints" element={<ComplaintPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/announcements" element={<AnnouncementsPage />} />
                <Route path="/logs" element={<ActivityLogsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <AuthProvider>
            <LanguageProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                </BrowserRouter>
            </LanguageProvider>
        </AuthProvider>
    );
}

export default App;
